import React from 'react'

const MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12']
const MONTH_LABELS = ['Led','Úno','Bře','Dub','Kvě','Čvn','Čvc','Srp','Zář','Říj','Lis','Pro']
const SIDS = [...MONTHS, 'YEAR']

function fmtVal(value, decimals) {
  if (value == null) return null
  if (decimals === 0) return String(Math.round(value))
  return value.toFixed(decimals ?? 1)
}

function fmtDiff(diff, normalMode, decimals) {
  if (normalMode === 'pct') return `${Math.round(diff)} %`
  const sign = diff > 0 ? '+' : ''
  return `${sign}${diff.toFixed(decimals ?? 1)}`
}

function diffCls(diff, normalMode) {
  const d = normalMode === 'pct' ? diff - 100 : diff
  if (d > 0.05) return 'text-red-600 font-medium'
  if (d < -0.05) return 'text-blue-600 font-medium'
  return 'text-gray-400'
}

function makeTip(valStr, normalStr, diff, normalMode, unit) {
  if (normalMode === 'pct') {
    return `${valStr} / normál ${normalStr} × 100 = ${Math.round(diff)} %`
  }
  const sign = diff > 0 ? '+' : ''
  return `${valStr} − normál ${normalStr} = ${sign}${diff.toFixed(1)} ${unit ?? ''}`
}

function diffOf(value, normal, normalMode) {
  return normalMode === 'pct' ? (value / normal * 100) : (value - normal)
}

// tsId končící _MAX/_MIN nese datum výskytu extrému (sloupec `dt` v odpovědi sdata_ts).
function isExtremeTsId(tsId) {
  return /_(MAX|MIN)$/.test(tsId)
}

function formatDt(dt) {
  if (!dt) return null
  const d = new Date(dt)
  if (Number.isNaN(d.getTime())) return null
  const day   = String(d.getUTCDate()).padStart(2, '0')
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  return `${day}.${month}.${d.getUTCFullYear()}`
}

const cb = 'border-b border-r border-gray-200 px-1 py-0.5 text-xs tabular-nums whitespace-nowrap'
const thBase = 'border-b border-b-gray-200 border-r border-r-gray-200 px-1 py-1 text-xs font-normal text-center bg-gray-50'
const thBase2 = `${thBase} border-b-gray-400`

export function MlyGrid({ rowGroups, rowGroupsTransposed, orientation = 'normal', dataMap, yearMap, dtMap, yearDtMap, normalsMap, yearNormalsMap }) {
  if (orientation === 'transposed') {
    return (
      <MlyGridTransposed
        rowGroups={rowGroupsTransposed ?? rowGroups}
        dataMap={dataMap}
        yearMap={yearMap}
        dtMap={dtMap}
        yearDtMap={yearDtMap}
        normalsMap={normalsMap}
        yearNormalsMap={yearNormalsMap}
      />
    )
  }

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            {/* Row 1: month names */}
            <tr>
              <th
                rowSpan={2}
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold min-w-[16rem] whitespace-nowrap"
              >
                Veličina
              </th>
              {MONTHS.map((m, i) => (
                <th
                  key={m}
                  colSpan={2}
                  className={`${thBase} border-t-2 border-t-gray-400 text-gray-500 ${i === 0 ? '' : ''}`}
                >
                  {MONTH_LABELS[i]}
                </th>
              ))}
              <th
                colSpan={2}
                className={`${thBase} border-t-2 border-t-gray-400 text-gray-500 border-l border-l-gray-400`}
              >
                Rok
              </th>
            </tr>
            {/* Row 2: hod/odch sub-headers */}
            <tr>
              {SIDS.flatMap((sid, i) => {
                const isYear = sid === 'YEAR'
                const bl = i === 0 ? '' : isYear ? 'border-l border-l-gray-400' : ''
                return [
                  <th key={`${sid}-h`} className={`${thBase2} text-gray-400 ${bl}`}>hod.</th>,
                  <th key={`${sid}-d`} className={`${thBase2} text-gray-300`}>odch.</th>,
                ]
              })}
            </tr>
          </thead>
          <tbody>
            {rowGroups.map((group, gi) => (
              <React.Fragment key={`g${gi}`}>
                {/* Group header */}
                <tr>
                  <td className="sticky left-0 z-10 bg-gray-100 border-b border-l border-l-gray-400 border-r border-r-gray-400 px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {group.label}
                  </td>
                  {SIDS.flatMap((sid, i) => {
                    const isYear = sid === 'YEAR'
                    const bl = i === 0 ? '' : isYear ? 'border-l border-l-gray-400' : ''
                    return [
                      <td key={`${sid}-v`} className={`${cb} bg-gray-100 ${bl}`} />,
                      <td key={`${sid}-d`} className={`${cb} bg-gray-100`} />,
                    ]
                  })}
                </tr>
                {/* Data rows */}
                {group.rows.map(row => (
                  <tr key={row.tsId} className="group hover:bg-slate-50">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l border-l-gray-400 border-r border-r-gray-400 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap">
                      {row.label}
                      {row.unit && (
                        <span className="text-gray-400 ml-1.5 text-[10px]">({row.unit})</span>
                      )}
                    </td>
                    {SIDS.flatMap((sid, i) => {
                      const isYear = sid === 'YEAR'
                      const value = isYear
                        ? (yearMap?.[row.tsId] ?? null)
                        : (dataMap?.[row.tsId]?.[sid] ?? null)
                      const normal = isYear
                        ? (yearNormalsMap?.[row.tsId] ?? null)
                        : (normalsMap?.[row.tsId]?.[sid] ?? null)
                      const dtVal = isExtremeTsId(row.tsId)
                        ? (isYear ? (yearDtMap?.[row.tsId] ?? null) : (dtMap?.[row.tsId]?.[sid] ?? null))
                        : null
                      const dtTip = formatDt(dtVal)
                      const bl = i === 0 ? '' : isYear ? 'border-l border-l-gray-400' : ''
                      const fv = fmtVal(value, row.decimals)

                      if (row.normalMode && value != null && normal != null) {
                        const diff = diffOf(value, normal, row.normalMode)
                        const fn = fmtVal(normal, row.decimals)
                        const tip = makeTip(fv, fn, diff, row.normalMode, row.unit)
                        return [
                          <td key={`${sid}-v`} title={dtTip ?? undefined} className={`${cb} text-right bg-white group-hover:bg-slate-50 ${bl}`}>
                            {fv}
                          </td>,
                          <td key={`${sid}-d`} title={tip} className={`${cb} text-right bg-white group-hover:bg-slate-50 ${diffCls(diff, row.normalMode)}`}>
                            {fmtDiff(diff, row.normalMode, row.decimals)}
                          </td>,
                        ]
                      }
                      return [
                        <td
                          key={`${sid}-v`}
                          title={dtTip ?? undefined}
                          className={`${cb} text-right bg-white group-hover:bg-slate-50 ${bl} ${value == null ? 'text-gray-300' : 'text-gray-700'}`}
                        >
                          {fv ?? '—'}
                        </td>,
                        <td key={`${sid}-d`} className={`${cb} bg-gray-50`} />,
                      ]
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Transponovaná varianta ─────────────────────────────────────────────────────
// řádky = 12 měsíců + Rok (řádek Rok zvýrazněný a oddělený), sloupce = veličiny.
// Hlavička sloupců: 1. úroveň = group.label (colSpan přes veličiny × 2), 2. úroveň = row.label
// s jednotkou "(°C)", 3. úroveň = hod./odch.
function MlyGridTransposed({ rowGroups, dataMap, yearMap, dtMap, yearDtMap, normalsMap, yearNormalsMap }) {
  const flatRows = rowGroups.flatMap(g => g.rows.map(r => ({ ...r, group: g.label })))

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-l-2 border-l-gray-400 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            {/* Row 1: group names */}
            <tr>
              <th
                rowSpan={3}
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-bottom text-gray-600 font-semibold whitespace-nowrap"
              >
                Období
              </th>
              {rowGroups.map((g, gi) => (
                <th
                  key={g.label}
                  colSpan={g.rows.length * 2}
                  className={`${thBase} border-t-2 border-t-gray-400 text-gray-600 font-semibold ${gi > 0 ? 'border-l border-l-gray-400' : ''}`}
                >
                  {g.label}
                </th>
              ))}
            </tr>
            {/* Row 2: veličina + jednotka */}
            <tr>
              {rowGroups.flatMap((g, gi) =>
                g.rows.map((row, ri) => (
                  <th
                    key={row.tsId}
                    colSpan={2}
                    className={`${thBase2} text-gray-500 ${gi > 0 && ri === 0 ? 'border-l border-l-gray-400' : ''}`}
                  >
                    {row.label}
                    {row.unit && <span className="text-gray-400 ml-1 text-[10px]">({row.unit})</span>}
                  </th>
                ))
              )}
            </tr>
            {/* Row 3: hod./odch. */}
            <tr>
              {rowGroups.flatMap((g, gi) =>
                g.rows.flatMap((row, ri) => [
                  <th key={`${row.tsId}-h`} className={`${thBase2} text-gray-400 ${gi > 0 && ri === 0 ? 'border-l border-l-gray-400' : ''}`}>hod.</th>,
                  <th key={`${row.tsId}-d`} className={`${thBase2} text-gray-300`}>odch.</th>,
                ])
              )}
            </tr>
          </thead>
          <tbody>
            {SIDS.map((sid, si) => {
              const isYear = sid === 'YEAR'
              const rowLabel = isYear ? 'Rok' : MONTH_LABELS[si]
              const topRule = isYear ? 'border-t-2 border-t-gray-400' : ''
              return (
                <tr key={sid} className="group hover:bg-slate-50">
                  <td className={`sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 group-last:border-b-0 px-3 py-0.5 text-left whitespace-nowrap ${isYear ? 'font-semibold text-gray-700' : 'text-gray-600'} ${topRule}`}>
                    {rowLabel}
                  </td>
                  {flatRows.flatMap((row, ri) => {
                    const firstOfGroup = ri > 0 && flatRows[ri - 1].group !== row.group
                    const bl = firstOfGroup ? 'border-l border-l-gray-400' : ''
                    const value = isYear
                      ? (yearMap?.[row.tsId] ?? null)
                      : (dataMap?.[row.tsId]?.[sid] ?? null)
                    const normal = isYear
                      ? (yearNormalsMap?.[row.tsId] ?? null)
                      : (normalsMap?.[row.tsId]?.[sid] ?? null)
                    const dtVal = isExtremeTsId(row.tsId)
                      ? (isYear ? (yearDtMap?.[row.tsId] ?? null) : (dtMap?.[row.tsId]?.[sid] ?? null))
                      : null
                    const dtTip = formatDt(dtVal)
                    const fv = fmtVal(value, row.decimals)

                    if (row.normalMode && value != null && normal != null) {
                      const diff = diffOf(value, normal, row.normalMode)
                      const fn = fmtVal(normal, row.decimals)
                      const tip = makeTip(fv, fn, diff, row.normalMode, row.unit)
                      return [
                        <td key={`${row.tsId}-v`} title={dtTip ?? undefined} className={`${cb} text-right bg-white group-hover:bg-slate-50 group-last:border-b-0 ${bl} ${topRule}`}>
                          {fv}
                        </td>,
                        <td key={`${row.tsId}-d`} title={tip} className={`${cb} text-right bg-white group-hover:bg-slate-50 group-last:border-b-0 ${diffCls(diff, row.normalMode)} ${topRule}`}>
                          {fmtDiff(diff, row.normalMode, row.decimals)}
                        </td>,
                      ]
                    }
                    return [
                      <td
                        key={`${row.tsId}-v`}
                        title={dtTip ?? undefined}
                        className={`${cb} text-right bg-white group-hover:bg-slate-50 group-last:border-b-0 ${bl} ${topRule} ${value == null ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        {fv ?? '—'}
                      </td>,
                      <td key={`${row.tsId}-d`} className={`${cb} bg-gray-50 group-last:border-b-0 ${topRule}`} />,
                    ]
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
